/**
 * <sn-scroll-pin>: while the page scrolls vertically through this element, its
 * sticky panel stays in view and the track slides horizontally (right-to-left in
 * RTL), so vertical scrolling reads the row. Falls back to a native swipeable row
 * when nothing overflows, under reduced motion, or on very short viewports, and
 * always uses that row with data-pin-mode="row". In the row, the optional
 * previous/next buttons step one item and the progress line follows the scroll.
 *
 * Markup contract:
 *   <sn-scroll-pin [data-pin-mode="row"]>
 *     <div data-pin-sticky> ... <div data-pin-viewport><ul data-pin-track><li data-pin-item>…
 *     <span data-pin-progress></span> (optional)
 *     <button data-pin-prev> <button data-pin-next> (optional)
 */
class SnScrollPin extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #observer = null;
  #frame = 0;
  #distance = 0;
  #active = false;
  /** @type {IntersectionObserver | null} */
  #approach = null;

  connectedCallback() {
    this.sticky = this.querySelector('[data-pin-sticky]');
    this.viewport = this.querySelector('[data-pin-viewport]');
    this.track = this.querySelector('[data-pin-track]');
    this.progress = this.querySelector('[data-pin-progress]');
    this.prev = this.querySelector('[data-pin-prev]');
    this.next = this.querySelector('[data-pin-next]');
    if (!this.sticky || !this.viewport || !this.track) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    window.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    this.viewport.addEventListener('scroll', () => this.#updateRow(), { passive: true, signal });
    this.prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.next?.addEventListener('click', () => this.#step(1), { signal });
    window.addEventListener('resize', () => this.#measure(), { signal });
    this.reducedMotion.addEventListener('change', () => this.#measure(), { signal });
    this.addEventListener('focusin', (event) => this.#reveal(event.target), { signal });

    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.viewport);
    this.#measure();

    // Off-screen items sit far along the horizontal axis; start loading them as
    // the row approaches so they are ready when they slide into view.
    this.#approach = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        this.#approach?.disconnect();
      },
      { rootMargin: '150px 0px' }
    );
    this.#approach.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#approach?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  #measure() {
    const distance = this.track.scrollWidth - this.viewport.clientWidth;
    const enable =
      this.dataset.pinMode !== 'row' && distance > 8 && !this.reducedMotion.matches && window.innerHeight >= 480;
    this.#distance = Math.max(0, distance);
    this.toggleAttribute('data-overflow', distance > 8);

    if (enable !== this.#active) {
      this.#active = enable;
      this.toggleAttribute('data-pinned', enable);
    }

    this.style.height = enable ? `${this.sticky.offsetHeight + this.#distance}px` : '';
    this.#update();
  }

  #schedule() {
    if (!this.#active) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #progress() {
    const rect = this.getBoundingClientRect();
    const travel = rect.height - this.sticky.offsetHeight;
    if (travel <= 0) return 0;
    return Math.min(1, Math.max(0, -rect.top / travel));
  }

  #update() {
    if (!this.#active) {
      this.track.style.transform = '';
      this.#updateRow();
      return;
    }
    const progress = this.#progress();
    const direction = this.#isRtl ? 1 : -1;
    this.track.style.transform = `translate3d(${(direction * progress * this.#distance).toFixed(1)}px, 0, 0)`;
    if (this.progress) this.progress.style.transform = `scaleX(${progress.toFixed(4)})`;
  }

  /** Native row: progress line and arrow states follow the row's own scroll position. */
  #updateRow() {
    if (this.#active) return;
    const max = this.viewport.scrollWidth - this.viewport.clientWidth;
    const position = Math.abs(this.viewport.scrollLeft);
    if (this.progress) this.progress.style.transform = `scaleX(${max > 0 ? (position / max).toFixed(4) : 0})`;
    if (this.prev) this.prev.disabled = position <= 2;
    if (this.next) this.next.disabled = position >= max - 2;
  }

  /** Scrolls the native row by one item towards its end (1) or its start (-1). */
  #step(direction) {
    const item = this.track.querySelector('[data-pin-item]');
    if (!item) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const amount = (item.getBoundingClientRect().width + gap) * direction * (this.#isRtl ? -1 : 1);
    this.viewport.scrollBy({ left: amount, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /** Keeps a keyboard-focused item visible by scrolling the page to its position. */
  #reveal(target) {
    if (!this.#active || !(target instanceof Element)) return;
    const item = target.closest('[data-pin-item]');
    if (!item || this.#distance === 0) return;

    const trackRect = this.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const fromStart = this.#isRtl ? trackRect.right - itemRect.right : itemRect.left - trackRect.left;
    const wanted = Math.min(this.#distance, Math.max(0, fromStart - (this.viewport.clientWidth - itemRect.width) / 2));
    const travel = this.offsetHeight - this.sticky.offsetHeight;
    const top = this.getBoundingClientRect().top + window.scrollY + (wanted / this.#distance) * travel;
    window.scrollTo({ top, behavior: 'instant' });
  }
}

if (!customElements.get('sn-scroll-pin')) customElements.define('sn-scroll-pin', SnScrollPin);
